import {
  createContext,
  type FC,
  type ReactNode,
  useCallback,
  useContext,
  useState,
} from "react";
import type { CustomerEmbeddedContext, EmbeddedContext } from "./types";

type ImplicitEmbeddedContextValue = {
  implicitContexts: EmbeddedContext[];
  addCustomer: (customer: { id: string; formattedName: string }) => void;
  removeCustomer: (customerId: string) => void;
};

const ImplicitEmbeddedContextContext =
  createContext<ImplicitEmbeddedContextValue | null>(null);

export const useImplicitEmbeddedContext = () => {
  const context = useContext(ImplicitEmbeddedContextContext);
  if (!context) {
    throw new Error(
      "useImplicitEmbeddedContext must be used within ImplicitEmbeddedContextProvider",
    );
  }
  return context;
};

type ImplicitEmbeddedContextProviderProps = {
  children: ReactNode;
};

export const ImplicitEmbeddedContextProvider: FC<
  ImplicitEmbeddedContextProviderProps
> = ({ children }) => {
  const [implicitContexts, setImplicitContexts] = useState<EmbeddedContext[]>(
    [],
  );

  const addCustomer = useCallback(
    (customer: { id: string; formattedName: string }) => {
      const customerContext = createCustomerEmbeddedContext(customer);
      setImplicitContexts((prev) => {
        // 既に同じ顧客のコンテキストがあるかチェック
        const exists = prev.some(
          (ctx): ctx is CustomerEmbeddedContext =>
            ctx.type === "customer" && ctx.id === customer.id,
        );
        if (exists) {
          return prev;
        }
        return [...prev, customerContext];
      });
    },
    [],
  );

  const removeCustomer = useCallback((customerId: string) => {
    setImplicitContexts((prev) =>
      prev.filter((ctx) => !(ctx.type === "customer" && ctx.id === customerId)),
    );
  }, []);

  return (
    <ImplicitEmbeddedContextContext.Provider
      value={{ implicitContexts, addCustomer, removeCustomer }}
    >
      {children}
    </ImplicitEmbeddedContextContext.Provider>
  );
};

/**
 * 顧客情報から EmbeddedContext を作成するユーティリティ
 */
export const createCustomerEmbeddedContext = (customer: {
  id: string;
  formattedName: string;
}): CustomerEmbeddedContext => {
  return {
    type: "customer",
    id: customer.id,
    name: customer.formattedName,
    implicit: true,
  };
};
